import React from 'react';

/**
 * Inline probability meter: where the fair probability sits on 0-100%, and how wide its
 * confidence interval is.
 *
 * Form is a **meter**, not a bar chart: one ratio against a fixed limit (0-100%), one per
 * market row. Single series, so no legend — the column header names it, and the numeric p̂
 * and CI sit beside it as the label (labels are selective by design; a number on every mark
 * would be noise in a table that already prints them).
 *
 * Colour is sequential, one hue (blue), per the meter rule that the unfilled track is a
 * lower step of the mark's own ramp so state reads across the whole bar:
 *   - CI band  #3987e5  (dark-surface slot-1 blue)
 *   - track    #184f95  (step 600 — the darkest step allowed to sit near a dark surface)
 * Validated as an adjacent pair on the dark surface: CVD ΔE 18.9 (deutan) / 19.2 (tritan),
 * normal-vision ΔE 19.2 — clear of the ≥8 / ≥15 floors. The track's 2.15:1 contrast is
 * deliberately recessive (it is a rail, not data); the visible numbers are the relief.
 *
 * Mark specs: 6px-thick marks with fully rounded data-ends, a 1px recessive hairline at
 * even money, and the point-estimate tick carrying a 2px ring in the surface colour so it
 * stays legible where it crosses the band.
 */

const WIDTH = 132;
const HEIGHT = 18;
const TRACK_Y = 6;
const TRACK_H = 6;
const TICK_H = 14;
const MIN_BAND_PX = 3;

const BAND = '#3987e5';
const TRACK = '#184f95';
const HAIRLINE = 'rgba(148, 163, 184, 0.45)';
const TICK = '#f1f5f9';
/** Ring/gap colour: matches the row surface so the ring reads as negative space. */
const SURFACE = '#111a2e';

const pct = (x) => `${(Number(x) * 100).toFixed(1)}%`;

export function clamp01(x) {
  // Reject nullish and empty string explicitly: Number(null) and Number('') are both 0, so
  // a missing bound would otherwise plot as 0% — a band drawn from zero to the estimate,
  // which reads as enormous certainty about a market that has no interval at all.
  if (x == null || x === '') return null;
  const n = Number(x);
  if (!Number.isFinite(n)) return null;
  return Math.min(1, Math.max(0, n));
}

/**
 * Pure geometry for the meter, so overflow and collisions are testable without a DOM.
 * Returns null when there is no probability to plot.
 */
export function meterGeometry(pFair, pLower, pUpper) {
  const p = clamp01(pFair);
  if (p == null) return null;
  const lo = clamp01(pLower);
  const hi = clamp01(pUpper);
  const hasBand = lo != null && hi != null && hi > lo;

  const x = (v) => v * WIDTH;
  const rawBandW = hasBand ? x(hi) - x(lo) : 0;
  // A near-degenerate interval still has to be visible, and the band must stay inside the
  // rail when the interval runs to either edge.
  const bandW = Math.max(MIN_BAND_PX, rawBandW);
  const bandLeft = Math.min(Math.max(hasBand ? x(lo) : x(p) - bandW / 2, 0), WIDTH - bandW);
  const tickX = x(p);
  const ringLeft = Math.min(Math.max(tickX - 3, 0), WIDTH - 6);
  const tickLeft = Math.min(Math.max(tickX - 1, 0), WIDTH - 2);

  return {
    width: WIDTH,
    height: HEIGHT,
    p,
    lo: hasBand ? lo : null,
    hi: hasBand ? hi : null,
    hasBand,
    bandLeft,
    bandW,
    ringLeft,
    tickLeft,
    title: hasBand
      ? `Fair ${pct(p)} · 90% CI ${pct(lo)}–${pct(hi)} (width ${(
          (hi - lo) * 100
        ).toFixed(1)} pts)`
      : `Fair ${pct(p)} · no interval`,
  };
}

export default function ProbabilityMeter({ pFair, pLower, pUpper, label }) {
  const g = meterGeometry(pFair, pLower, pUpper);
  if (g == null) return <span className="prob-meter-empty">—</span>;
  const { bandLeft, bandW, ringLeft, tickLeft, title } = g;

  return (
    <svg
      className="prob-meter"
      width={WIDTH}
      height={HEIGHT}
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      role="img"
      aria-label={`${label ? `${label}. ` : ''}${title}`}
    >
      <title>{title}</title>
      {/* Rail: 0-100%. */}
      <rect
        x={0}
        y={TRACK_Y}
        width={WIDTH}
        height={TRACK_H}
        rx={TRACK_H / 2}
        fill={TRACK}
      />
      {/* Even money, hairline and recessive — it carries the reference the axis would. */}
      <line
        x1={WIDTH / 2}
        y1={TRACK_Y - 2}
        x2={WIDTH / 2}
        y2={TRACK_Y + TRACK_H + 2}
        stroke={HAIRLINE}
        strokeWidth={1}
      />
      {/* Confidence interval. */}
      <rect
        x={bandLeft}
        y={TRACK_Y}
        width={bandW}
        height={TRACK_H}
        rx={TRACK_H / 2}
        fill={BAND}
      />
      {/* Point estimate: 2px tick inside a 2px surface ring. */}
      <rect
        x={ringLeft}
        y={(HEIGHT - TICK_H) / 2}
        width={6}
        height={TICK_H}
        rx={1}
        fill={SURFACE}
      />
      <rect
        x={tickLeft}
        y={(HEIGHT - TICK_H) / 2}
        width={2}
        height={TICK_H}
        rx={1}
        fill={TICK}
      />
    </svg>
  );
}
